import { useEffect, useState } from 'react'
import cn from 'classnames'
import { useAppStore } from '@forwardflow/store/appStore'
import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import { UserService } from '@forwardflow/services/UserService'
import { useGetUserCalenderInfo } from '@forwardflow/hooks/useGetUserCalenderInfo'
import { Screen } from '../frame/SetupFrame'
import { useStepFooter } from '../../hooks/useStepFooter'
import { ErrorLine, Spinner } from '../ui/primitives'
import { GoogleG, Icon, MicrosoftM } from '../ui/Icon'
import { errorText } from '../../utils/format'
import { WIZARD_PATH } from '../../utils/constants'

type TProvider = 'google' | 'microsoft'

/**
 * Step 4: where appointments go.
 *
 * The provider's sign-in opens and comes straight back to the wizard; the
 * loader then sees the connected calendar. Skipping is fine: appointments
 * are written into Follow Up Boss until a calendar is connected.
 */
export const CalendarStep = () => {
   const { currentOrganization } = useAppStore()
   const { onboardingData, getNextStep, setIsCalendarConnected } =
      useOnboardingStoreV2()
   const { data: info } = useGetUserCalenderInfo(Boolean(currentOrganization))
   const provider: TProvider | null = info?.email
      ? info.provider === 'microsoft'
         ? 'microsoft'
         : 'google'
      : null
   const connected = Boolean(provider) || onboardingData.isCalendarConnected
   const [connecting, setConnecting] = useState<TProvider | null>(null)
   const [error, setError] = useState<string | null>(null)

   useEffect(() => {
      if (info?.email) setIsCalendarConnected(true)
   }, [info?.email, setIsCalendarConnected])

   const connect = async (p: TProvider) => {
      setConnecting(p)
      setError(null)
      try {
         const redirectUrl = encodeURIComponent(
            `${window.location.origin}${WIZARD_PATH}`
         )
         const response =
            p === 'microsoft'
               ? await UserService.getConnectUserWithOutlookCalendar(
                    redirectUrl,
                    currentOrganization || ''
                 )
               : await UserService.getConnectUserWithCalender(
                    redirectUrl,
                    currentOrganization || ''
                 )
         if (response?.redirectUrl) {
            window.location.href = response.redirectUrl
            return
         }
         setError('No sign-in link came back. Try again in a moment.')
      } catch (e) {
         setError(
            errorText(e, "We couldn't start the calendar sign-in. Try again.")
         )
      }
      setConnecting(null)
   }

   useStepFooter(
      connected ? 'Continue' : 'Choose a calendar to continue',
      () => getNextStep(),
      {
         disabled: !connected,
         secondary: connected
            ? null
            : {
                 label: 'Skip for now, book appointments into Follow Up Boss',
                 onClick: () => getNextStep(),
              },
         hint: connected
            ? 'Appointments land on your calendar and in Follow Up Boss.'
            : 'This opens the provider’s sign-in. One tap.',
         testId: 'continue-calendar',
      }
   )

   const Provider = ({
      id,
      icon,
      label,
   }: {
      id: TProvider
      icon: React.ReactNode
      label: string
   }) => {
      const on =
         provider === id || (!provider && connected && id === 'google' && !info)
      const busy = connecting === id
      return (
         <button
            type="button"
            onClick={() => (on ? undefined : void connect(id))}
            disabled={Boolean(connecting) || (connected && !on)}
            data-testid={`calendar-${id}`}
            className={cn(
               'w-full flex items-center gap-4 rounded-[20px] border bg-[var(--ob-surface)] px-5 h-[72px] text-left transition-all',
               on
                  ? 'border-[var(--ob-good)] ring-4 ring-[var(--ob-good-tint)]'
                  : 'border-[var(--ob-border)] hover:border-[var(--ob-border-strong)] disabled:opacity-50'
            )}
         >
            <span className="flex items-center justify-center w-10 h-10 rounded-full bg-white/90">
               {icon}
            </span>
            <span className="flex-1 min-w-0">
               <span className="block text-[15px] font-semibold">
                  {on ? `${label} connected` : `Continue with ${label}`}
               </span>
               <span className="block text-[13px] text-[var(--ob-text-2)] truncate">
                  {on
                     ? info?.email ||
                       'Bookings land here with the lead’s details'
                     : 'Opens the sign-in, then comes straight back'}
               </span>
            </span>
            {on ? (
               <span className="text-[var(--ob-good)]">
                  <Icon name="check" size={20} />
               </span>
            ) : busy ? (
               <span className="text-[var(--ob-text-3)]">
                  <Spinner size={18} />
               </span>
            ) : (
               <span className="text-[var(--ob-text-3)]">
                  <Icon name="arrow-right" size={18} />
               </span>
            )}
         </button>
      )
   }

   return (
      <Screen
         stepKey="calendar-connection"
         kind="calendar"
         title="Where should appointments go?"
         lede="When a lead is ready to meet and you can’t pick up, we book the appointment for you. Sign in to the calendar we should use."
         width="sm"
      >
         <div className="space-y-3">
            <Provider id="google" icon={<GoogleG />} label="Google Calendar" />
            <Provider
               id="microsoft"
               icon={<MicrosoftM />}
               label="Outlook Calendar"
            />
         </div>
         {error && <ErrorLine testId="calendar-error">{error}</ErrorLine>}
      </Screen>
   )
}
